import { Title } from '@solidjs/meta'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Table from '~/components/Table'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import AIConnectionsManager from '~/layers/AIConnectionsManager'
import AIProviderCard from '~/layers/AIProviderCard'
import { useNavigate } from '~/router'
import {
  type AgentScopeType,
  type AgentTypeType,
  type AIAgentType,
  type AICategoryType,
  type AIProviderCapabilityType,
  aiService,
  type ProviderCatalogEntryType,
  type SaveAgentPayloadType
} from '~/services/ai.service'
import type { ColumnType } from '~/types/Table'
import { toolLabel } from '~/utils/chat'
import { resolveError } from '~/utils/error'

const SCOPE_OPTIONS = [
  { value: 'internal', title: 'Interno', subtitle: 'Sólo el chat del panel' },
  { value: 'public', title: 'Web pública', subtitle: 'Sólo el widget de la web' },
  { value: 'both', title: 'Ambos', subtitle: 'Panel y web' }
]

const AVATAR_CHOICES = ['🤖', '💼', '🧑‍💻', '📊', '🎯', '🛠️', '📚', '⚡', '🧠', '🌟']

/**
 * Las cinco capacidades que soporta el backend. Cada una lleva SU proveedor y SU
 * modelo, y son independientes: el chat puede ir en un servicio de pago y los
 * embeddings en un Ollama local. Antes esta página sólo dejaba tocar el chat.
 */
const CATEGORIES: { category: AICategoryType; title: string; subtitle: string; icon: string }[] = [
  { category: 'chat', title: 'Chat', subtitle: 'Conversaciones del panel y del asesor web', icon: 'material-symbols-light:chat-outline' },
  { category: 'image', title: 'Imágenes', subtitle: 'Generar imágenes a partir de texto', icon: 'material-symbols-light:image-outline' },
  { category: 'audio', title: 'Audio', subtitle: 'Voz en tiempo real (Gemini Live, OpenAI Realtime)', icon: 'material-symbols-light:mic-outline' },
  { category: 'document', title: 'Documentos', subtitle: 'Leer y analizar ficheros', icon: 'material-symbols-light:description-outline' },
  { category: 'embedding', title: 'Embeddings', subtitle: 'Vectores para buscar por significado', icon: 'material-symbols-light:widgets-outline' }
]

/**
 * Quién sabe hacer qué. Sale del catálogo del backend, no de una lista a mano.
 * Las checkboxes se generan dinámicamente según las capacidades de la empresa.
 */
const buildCatalogColumns = (args: {
  getCapabilities: () => AIProviderCapabilityType[] | undefined
  onToggle: (provider: string, category: string, enabled: boolean) => void
}): ColumnType<ProviderCatalogEntryType>[] => [
  {
    header: 'Proveedor',
    accessor: p => (
      <div class="flex items-center gap-2">
        <span class="font-medium text-foreground">{p.label}</span>
        <Show when={!p.requiresApiKey}>
          <Chip size="sm" variant="soft" color="success">
            sin clave
          </Chip>
        </Show>
      </div>
    )
  },
  ...CATEGORIES.map(cat => ({
    header: (
      <div class="flex items-center justify-center gap-1">
        <Icon icon={cat.icon} size={14} />
        {cat.title}
      </div>
    ),
    accessor: (p: ProviderCatalogEntryType) => {
      if (!p.categories.includes(cat.category)) {
        return (
          <span class="text-foreground-subtle/40" title={`${p.label} no hace ${cat.title.toLowerCase()}`}>
            —
          </span>
        )
      }
      const caps = args.getCapabilities()
      const enabled = caps === undefined ? true : !caps.some(c => c.provider === p.name && c.category === cat.category && c.enabled === 0)
      return (
        <div class="flex items-center justify-center">
          <Checkbox
            checked={enabled}
            size="sm"
            color="primary"
            title={enabled ? `Deshabilitar ${cat.title.toLowerCase()} para ${p.label}` : `Habilitar ${cat.title.toLowerCase()} para ${p.label}`}
            onChange={() => args.onToggle(p.name, cat.category, !enabled)}
          />
        </div>
      )
    },
    cellClass: 'text-center'
  }))
]

const TYPE_OPTIONS = [
  { value: 'text', title: 'Texto', subtitle: 'Responde por el chat' },
  { value: 'voice', title: 'Voz', subtitle: 'Responde hablando (Gemini Live)' }
]

const emptyAgent: SaveAgentPayloadType = {
  name: '',
  description: '',
  avatar: '🤖',
  systemPrompt: '',
  model: '',
  temperature: null,
  maxTokens: null,
  tools: null,
  scope: 'internal',
  type: 'text',
  connectionId: null,
  acceptsImages: false,
  isDefault: false
}

const IAConfigPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()

  const [catalog] = createResource(async () => {
    try {
      return await aiService.providerCatalog()
    } catch {
      return []
    }
  })
  const [providers, { refetch: refetchProviders }] = createResource(async () => {
    try {
      return await aiService.listProviders()
    } catch {
      return []
    }
  })
  const [connections, { refetch: refetchConnections }] = createResource(async () => {
    try {
      return await aiService.listConnections()
    } catch {
      return []
    }
  })
  const [connectionModels, { refetch: refetchModels }] = createResource(async () => {
    try {
      return await aiService.allConnectionModels()
    } catch {
      return []
    }
  })
  const [agents, { refetch: refetchAgents }] = createResource(async () => {
    try {
      return await aiService.listAgents()
    } catch {
      return []
    }
  })
  const [tools] = createResource(async () => {
    try {
      return await aiService.listTools()
    } catch {
      return []
    }
  })
  const [capabilities, { refetch: refetchCapabilities }] = createResource(async () => {
    try {
      return await aiService.listCapabilities()
    } catch {
      return []
    }
  })

  // Las credenciales viven en Conexiones (AIConnectionsManager); cada capacidad
  // (AIProviderCard) elige una y su modelo. Aquí sólo se cargan y se pasan.
  const refetchConfig = async () => {
    await Promise.all([refetchConnections(), refetchProviders(), refetchCapabilities(), refetchModels()])
  }

  // ── Capacidades por proveedor ──────────────────────────────────────────
  const toggleCapability = async (provider: string, category: string, enabled: boolean) => {
    try {
      await aiService.toggleCapability(provider, category, enabled)
      await refetchCapabilities()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const catalogColumns = createMemo(() =>
    buildCatalogColumns({
      getCapabilities: () => capabilities(),
      onToggle: toggleCapability
    })
  )

  // ── Agentes ────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<AIAgentType | null>(null)

  // Vacío significa "sin valor": el backend espera null, no una cadena.
  const optionalNumber = (message: string, extra?: (schema: z.ZodNumber) => z.ZodNumber) =>
    z.union([z.literal(''), z.null(), extra ? extra(z.coerce.number({ message })) : z.coerce.number({ message })]).transform(value => (value === '' || value === null ? null : Number(value)))

  const agentSchema = z
    .object({
      name: z.string().trim().min(1, 'El nombre es obligatorio'),
      description: z.string().trim().default(''),
      avatar: z.string().default('🤖'),
      systemPrompt: z.string().trim().min(1, 'La personalidad es obligatoria'),
      model: z
        .string()
        .trim()
        .default('')
        .transform(value => value || null),
      temperature: optionalNumber('Temperatura inválida', s => s.min(0, 'Mínimo 0').max(2, 'Máximo 2')),
      maxTokens: optionalNumber('Máximo de tokens inválido', s => s.int('Debe ser un entero').min(1, 'Mínimo 1')),
      tools: z.array(z.string()).nullable().default(null),
      scope: z.enum(['internal', 'public', 'both']).default('internal'),
      type: z.enum(['text', 'voice']).default('text'),
      connectionId: optionalNumber('Conexión inválida'),
      acceptsImages: z.boolean().default(false),
      isDefault: z.boolean().default(false)
    })
    // Un agente de voz sin conexión no tendría con quién hablar: el chat de voz
    // no cae al proveedor por defecto, porque ese puede no hacer audio.
    .refine(data => data.type !== 'voice' || data.connectionId !== null, {
      message: 'Un agente de voz necesita una conexión con audio (Gemini)',
      path: ['connectionId']
    })

  const form = useForm({
    schema: agentSchema,
    initialValues: () => {
      const a = editing()
      if (!a) return { ...emptyAgent, model: '', temperature: '', maxTokens: '', connectionId: '' }

      return {
        name: a.name,
        description: a.description ?? '',
        avatar: a.avatar ?? '🤖',
        systemPrompt: a.systemPrompt,
        model: a.model ?? '',
        temperature: a.temperature ?? '',
        maxTokens: a.maxTokens ?? '',
        tools: a.tools ?? null,
        scope: a.scope ?? 'internal',
        type: a.type ?? 'text',
        connectionId: a.connectionId != null ? String(a.connectionId) : '',
        acceptsImages: Boolean(a.acceptsImages),
        isDefault: Boolean(a.isDefault)
      }
    },
    onSubmit: async values => {
      const payload = { ...values, description: values.description || undefined } as SaveAgentPayloadType

      if (editing()) {
        await aiService.updateAgent(editing()!.id, payload)
        toast.addToast({ title: 'Agente actualizado', type: 'success' })
      } else {
        await aiService.createAgent(payload)
        toast.addToast({ title: 'Agente creado', type: 'success' })
      }
      setShowModal(false)
      await refetchAgents()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const openCreate = () => {
    setEditing(null)
    form.reset()
    setShowModal(true)
  }

  const openEdit = (a: AIAgentType) => {
    setEditing(a)
    setShowModal(true)
  }

  const handleDeleteAgent = async (a: AIAgentType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar agente',
      message: `¿Eliminar el agente "${a.name}"?`
    })
    if (!ok) return
    try {
      await aiService.deleteAgent(a.id)
      toast.addToast({ title: 'Agente eliminado', type: 'success' })
      await refetchAgents()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const currentTools = () => (form.values().tools as string[] | null | undefined) ?? null

  const toggleTool = (name: string) => {
    const current = currentTools()
    if (current === null) {
      // null = todas permitidas; al desmarcar una, hay que materializar el resto.
      const all = (tools() ?? []).map(t => t.name)
      form.setValue(
        'tools',
        all.filter(t => t !== name)
      )
      return
    }
    form.setValue('tools', current.includes(name) ? current.filter(t => t !== name) : [...current, name])
  }

  const isToolEnabled = (name: string) => {
    const current = currentTools()
    return current === null ? true : current.includes(name)
  }

  const scopeLabel = (scope?: AgentScopeType) => SCOPE_OPTIONS.find(o => o.value === scope)?.title ?? 'Interno'

  /**
   * Con qué conexiones puede hablar el agente según su tipo: uno de voz sólo con
   * proveedores que hacen audio (Gemini), uno de texto con los que hacen chat.
   * Es lo que impide crear un "agente de voz" apuntando a un proveedor sin audio.
   */
  const agentType = () => (form.values().type as AgentTypeType | undefined) ?? 'text'

  const agentConnectionOptions = () => {
    const needed = agentType() === 'voice' ? 'audio' : 'chat'
    const valid = new Set((catalog() ?? []).filter(p => p.categories.includes(needed as AICategoryType)).map(p => p.name))
    const list = (connections() ?? []).filter(c => valid.has(c.provider))
    return [{ value: '', title: agentType() === 'voice' ? '— elige una conexión —' : 'Por defecto de la categoría' }, ...list.map(c => ({ value: String(c.id), title: c.label }))]
  }

  /**
   * Modelos que ofrece la cuenta elegida.
   *
   * Antes esto era un campo de texto libre: un typo no se descubría hasta que
   * alguien usaba el chat y el proveedor devolvía un 400. La lista sale de lo
   * que respondió el propio proveedor al probar la conexión.
   */
  const agentModelOptions = () => {
    const connId = Number(form.values().connectionId) || null
    const vacio = { value: '', title: connId ? 'El que sugiera el proveedor' : 'El de la capacidad' }
    if (!connId) return [vacio]

    const modelos = (connectionModels() ?? []).filter(m => m.connectionId === connId && m.available)
    const actual = String(form.values().model ?? '')

    // Un modelo guardado que el proveedor ya no lista sigue apareciendo, marcado:
    // esconderlo haría que al guardar el formulario se perdiera sin avisar.
    const retirado = actual && !modelos.some(m => m.model === actual) ? [{ value: actual, title: `${actual} (ya no disponible)` }] : []

    return [vacio, ...retirado, ...modelos.map(m => ({ value: m.model, title: m.model }))]
  }

  /** ¿La cuenta elegida tiene modelos guardados? Si no, hay que probarla antes. */
  const agentModelsKnown = () => {
    const connId = Number(form.values().connectionId) || null
    if (!connId) return true
    return (connectionModels() ?? []).some(m => m.connectionId === connId)
  }

  const columns = [
    { header: '', accessor: (a: AIAgentType) => a.avatar ?? '🤖' },
    { header: 'Agente', accessor: (a: AIAgentType) => a.name },
    {
      header: 'Tipo',
      accessor: (a: AIAgentType) => (
        <Chip size="sm" variant="soft" color={a.type === 'voice' ? 'info' : 'default'} icon={a.type === 'voice' ? 'material-symbols-light:mic-outline' : 'material-symbols-light:chat-outline'}>
          {a.type === 'voice' ? 'Voz' : 'Texto'}
        </Chip>
      )
    },
    {
      header: 'Cuenta',
      // Con varias cuentas del mismo proveedor, saber cuál usa cada agente deja
      // de ser un detalle: es lo que decide a quién se le factura el consumo.
      accessor: (a: AIAgentType) => connections()?.find(c => c.id === a.connectionId)?.label ?? 'la de la capacidad'
    },
    { header: 'Modelo', accessor: (a: AIAgentType) => a.model || 'por defecto' },
    {
      header: 'Herramientas',
      accessor: (a: AIAgentType) => (a.tools === null || a.tools === undefined ? 'todas' : `${a.tools.length}`)
    },
    { header: 'Dónde se usa', accessor: (a: AIAgentType) => scopeLabel(a.scope) },
    { header: 'Por defecto', accessor: (a: AIAgentType) => (a.isDefault ? 'Sí' : '-') },
    { header: 'Estado', accessor: (a: AIAgentType) => (a.status === 1 ? 'Activo' : 'Inactivo') }
  ]

  return (
    <Container>
      <Title>Configuración de IA | Florez Jaramillo</Title>

      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-foreground">Configuración de IA</h1>
          <p class="text-sm text-foreground-subtle">Proveedor y agentes de esta empresa</p>
        </div>
        <Button variant="soft" color="default" onClick={() => navigate('/admin/chat')} prependIcon="material-symbols-light:chat-outline">
          Abrir chat
        </Button>
      </div>

      <div class="grid grid-cols-1 gap-6 mt-4">
        <Show when={providers.error}>
          <Alert variant="danger">No se pudo cargar: {resolveError(providers.error)}</Alert>
        </Show>
        {/* `title` acepta JSX, así que el icono no se pierde al dejar que sea la
            propia Table quien pinte su cabecera en vez de envolverla en un Card. */}
        <Table<ProviderCatalogEntryType>
          title={
            <span class="flex items-center gap-2">
              <Icon icon="material-symbols-light:bolt-outline" size={20} />
              Qué sabe hacer cada proveedor
            </span>
          }
          subtitle="Ninguno lo hace todo. Cada capacidad se configura por separado, así que puedes tener el chat en uno de pago y los embeddings en un Ollama local."
          data={catalog() ?? []}
          columns={catalogColumns()}
          keyField="name"
          loading={catalog.loading}
        />

        {/* Paso 1: registrar las credenciales de cada proveedor, una vez. */}
        <AIConnectionsManager catalog={catalog() ?? []} connections={connections() ?? []} models={connectionModels() ?? []} onChanged={refetchConfig} />

        {/* Paso 2: una tarjeta por capacidad, cada una eligiendo una conexión.
            Verlas todas a la vez hace evidente qué falta; en pestañas, no. */}
        <div>
          <h2 class="text-xl font-semibold text-foreground mb-1">Capacidades</h2>
          <p class="text-sm text-foreground-subtle mb-3">Qué conexión y modelo usa cada cosa. Son independientes: el chat en una, los embeddings en otra.</p>
          <div class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
            <For each={CATEGORIES}>
              {cat => (
                <AIProviderCard
                  category={cat.category}
                  title={cat.title}
                  subtitle={cat.subtitle}
                  icon={cat.icon}
                  catalog={catalog() ?? []}
                  connections={connections() ?? []}
                  models={connectionModels() ?? []}
                  capabilities={capabilities() ?? []}
                  config={providers()?.find(p => p.category === cat.category)}
                  onSaved={refetchConfig}
                />
              )}
            </For>
          </div>
        </div>

        {/* La Table ya se pinta dentro de su propia tarjeta y tiene cabecera y
            estado de carga propios: envolverla en un Card dejaba un marco dentro
            de otro y duplicaba el título. */}
        <Show when={agents.error}>
          <Alert variant="danger">{resolveError(agents.error)}</Alert>
        </Show>
        <Table<AIAgentType>
          title="Agentes"
          subtitle="Cada agente tiene su nombre, personalidad y herramientas. Elige cuál usar desde el chat."
          headerActions={
            <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
              Nuevo agente
            </Button>
          }
          data={agents() ?? []}
          columns={columns}
          keyField="id"
          loading={agents.loading}
          emptyMessage="Aún no hay agentes"
          actions={item => (
            <ButtonGroup size="sm" variant="soft">
              <Button color="warning" onClick={() => openEdit(item)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
              <Button color="danger" onClick={() => handleDeleteAgent(item)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
            </ButtonGroup>
          )}
        />
      </div>

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar agente' : 'Nuevo agente'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="agent-form" controller={form}>
            <div class="space-y-4">
              <div>
                <span class="block text-sm font-medium text-foreground-muted mb-2">Avatar</span>
                <div class="flex flex-wrap gap-2">
                  <For each={AVATAR_CHOICES}>
                    {emoji => (
                      <Chip
                        color={form.values().avatar === emoji ? 'primary' : 'default'}
                        variant="soft"
                        size="md"
                        class="cursor-pointer select-none text-lg"
                        onClick={() => form.setValue('avatar', emoji)}
                      >
                        {emoji}
                      </Chip>
                    )}
                  </For>
                </div>
              </div>

              <Input name="name" label="Nombre" />
              <Input name="description" label="Descripción" helperText="En el asesor público es el saludo que ve el visitante." />

              <Textarea
                name="systemPrompt"
                label="Personalidad (instrucción de sistema)"
                rows={8}
                placeholder="Eres el asistente de la empresa. Respondes en español…"
                helperText="Define cómo se comporta. Los mensajes de sistema que envíe el cliente se ignoran."
              />

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Select
                    name="type"
                    label="Tipo"
                    options={TYPE_OPTIONS}
                    value={agentType()}
                    // La conexión válida depende del tipo: al cambiarlo, se resetea.
                    onChange={() => form.setValue('connectionId', '')}
                  />
                  <Show when={agentType() === 'voice'}>
                    <p class="text-xs text-foreground-subtle mt-1">Responde con audio. Necesita una conexión de un proveedor con voz (Gemini Live).</p>
                  </Show>
                </div>

                <div>
                  <Select
                    name="connectionId"
                    label="Cuenta de IA"
                    options={agentConnectionOptions()}
                    value={(form.values().connectionId as string | number | null) ?? ''}
                    // El modelo pertenece a la cuenta: al cambiarla, el anterior
                    // puede no existir en el proveedor nuevo.
                    onChange={() => form.setValue('model', '')}
                  />
                  <p class="text-xs text-foreground-subtle mt-1">
                    <Show when={agentType() === 'voice'} fallback="Con qué proveedor habla. Vacío = el que uses para el chat.">
                      Obligatoria para un agente de voz.
                    </Show>
                  </p>
                </div>
              </div>

              <div>
                <Select name="scope" label="Dónde se usa" options={SCOPE_OPTIONS} value={(form.values().scope as AgentScopeType) ?? 'internal'} />
                <p class="text-xs text-foreground-subtle mt-1">En la web sólo podrá usar herramientas públicas: nunca datos de clientes ni cotizaciones. Sólo un agente puede atender la web.</p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Select name="model" label="Modelo" options={agentModelOptions()} value={(form.values().model as string) ?? ''} />
                  <Show when={!agentModelsKnown()}>
                    <p class="text-xs text-warning mt-1">Prueba la conexión para conocer sus modelos.</p>
                  </Show>
                </div>
                <Input name="temperature" label="Temperatura" type="number" step="0.1" min="0" max="2" placeholder="por defecto" helperText="Los modelos Claude Opus 4.7+ la ignoran." />
                <Input name="maxTokens" label="Máx. tokens" type="number" min="1" placeholder="por defecto" />
              </div>

              <Show when={tools()}>
                <div>
                  <span class="block text-sm font-medium text-foreground-muted mb-2">Herramientas permitidas</span>
                  <div class="flex flex-wrap gap-2">
                    <For each={tools()}>
                      {tool => (
                        <Chip
                          color={isToolEnabled(tool.name) ? 'primary' : 'default'}
                          variant="soft"
                          size="sm"
                          icon={isToolEnabled(tool.name) ? 'material-symbols-light:check-circle-outline' : undefined}
                          title={tool.description}
                          class="cursor-pointer select-none"
                          onClick={() => toggleTool(tool.name)}
                        >
                          {toolLabel(tool.name)}
                        </Chip>
                      )}
                    </For>
                  </div>
                  <p class="text-xs text-foreground-subtle mt-1">{currentTools() === null ? 'Todas permitidas' : `${currentTools()?.length ?? 0} seleccionada(s)`}</p>
                </div>
              </Show>

              <Show when={agentType() !== 'voice'}>
                <div>
                  <Checkbox name="acceptsImages" label="Su modelo acepta imágenes (visión)" checked={Boolean(form.values().acceptsImages)} />
                  <p class="text-xs text-foreground-subtle mt-1 ml-7">
                    Habilita adjuntar imágenes en el chat. Márcalo sólo si el modelo es de visión (p.ej. mimo-v2-omni); la API no lo indica sola. Los documentos de texto funcionan con cualquier
                    modelo.
                  </p>
                </div>
              </Show>

              <Checkbox name="isDefault" label="Agente por defecto del chat interno" checked={Boolean(form.values().isDefault)} />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="agent-form" disabled={form.submitting() || !form.valid()}>
              {form.submitting() ? 'Guardando...' : editing() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </Container>
  )
}

export default IAConfigPage
